import { G } from '@mobily/ts-belt';
import { z } from 'zod';

import { ZodAmount, ZodAmountRequest } from '@Common/types/Amount';
import { TAIGA_PREFIX } from '@Common/types/b2b/axa/common';
import { ZodB2bSavingPlanCategory } from '@Common/types/b2b/plans';
import {
  ZodB2bRecurringInvestmentFrequency,
  ZodMutualFundOrderAgreedTerms,
  ZodMutualFundOrderAllocations,
  ZodVoluntaryDeductibility,
} from '@Common/types/explore/mutual_fund_order';
import { ZodB2BPaymentMethod } from '@Common/types/funding/payment_methods';
import { ZodTransformedProfileSavingPlanId } from '@Common/types/profile/saving_plans';
import { ZodProfileActivityTransaction } from '@Common/types/profile/transactions';
import { ZodCursorQuery, ZodId } from '@Common/types/request';
import { ZodCursorData, ZodSuccessResponse } from '@Common/types/response';
import { ZodDateTimeString } from '@Common/types/zod/zod-date-string';
import { endpoint, GetEndpointType } from '@Common/types/zod/zod-endpoint';
import { createZodParamsValidator } from '@Common/types/zod/zod-params';
import { ZodTrimmedString } from '@Common/types/zod/zod-trimmed-string';

export const ZodTaigaRecurringInvestmentId = z
  .string()
  .transform((id, ctx) => {
    try {
      return decodeURIComponent(id);
    } catch {
      ctx.addIssue({ code: 'custom', message: 'Invalid Taiga recurring investment ID' });
      return z.NEVER;
    }
  })
  .pipe(ZodTrimmedString.regex(new RegExp(`^${TAIGA_PREFIX}_[a-zA-Z0-9]+/[a-zA-Z0-9]+_[0-9]+$`)))
  .brand('TaigaRecurringInvestmentId')
  .meta({ override: { type: 'string' } });

export type TaigaRecurringInvestmentId = z.infer<typeof ZodTaigaRecurringInvestmentId>;

export const ZodRecurringInvestmentId = z.union([ZodId, ZodTaigaRecurringInvestmentId]);
export type RecurringInvestmentId = z.infer<typeof ZodRecurringInvestmentId>;

const ZodRecurringInvestmentIdPathParam = createZodParamsValidator(
  z.object({ id: ZodRecurringInvestmentId }),
);

export const ZodB2bRecurringInvestmentExecutionsStatus = z.enum([
  'pending',
  'shares_canceled',
  'executed',
  'skipped',
]);

export const B2bRecurringInvestmentsPaymentMethodValues = [
  'regular_bank',
  'sepa_direct_debit',
  'card',
  'apple_pay',
] as const;

/**
 * Mirrors the b2b_recurring_investments_payment_method_type DB enum
 */
export const ZodB2bRecurringInvestmentsPaymentMethod = z.enum(
  B2bRecurringInvestmentsPaymentMethodValues,
);

export type B2bRecurringInvestmentsPaymentMethod = z.infer<
  typeof ZodB2bRecurringInvestmentsPaymentMethod
>;

export const ZodB2bRecurringInvestmentExecutionsNotExecutedReason = z.enum([
  'payment_unable_to_charge',
  'employee_left_company',
  'sepa_dd_missing_mandate',
  'execution_canceled',
  'no_allocation_executed',
  'general_failure',
  'account_restriction',
]);

export const ZodB2bRecurringInvestmentsAgreedTerms = z.enum([
  ...ZodMutualFundOrderAgreedTerms.exclude(['legal_bonus_information']).options,
  'recurring_payment',
]);

export type B2bRecurringInvestmentsAgreedTerms = z.infer<
  typeof ZodB2bRecurringInvestmentsAgreedTerms
>;

const ZodRecurringInvestmentCommonBody = z.object({
  saving_plan_id: ZodTransformedProfileSavingPlanId,
  payment_method: ZodB2BPaymentMethod.exclude(['regular-bank', 'card', 'apple-pay']),
  agreed_terms: ZodB2bRecurringInvestmentsAgreedTerms.array(),
  deductibility: ZodVoluntaryDeductibility.optional(),
  start_date: ZodDateTimeString.optional(),
  frequency: ZodB2bRecurringInvestmentFrequency,
  rib_id: z.number().optional(),
});

const ZodRecurringInvestmentBody = z
  .object({
    ...ZodRecurringInvestmentCommonBody.shape,
    user_currency_total_value: ZodAmountRequest,
  })
  .or(
    z.object({
      ...ZodRecurringInvestmentCommonBody.shape,
      allocations: ZodMutualFundOrderAllocations,
    }),
  )
  .refine(
    // ER start dates are computed by the provider (BR_API240_230), only internal plans require one
    ({ saving_plan_id, start_date }) => !G.isObject(saving_plan_id) || G.isNotNullable(start_date),
    'start_date_is_required_for_internal_plans',
  );

export const postExploreRecurringInvestmentsEndpoint = endpoint({
  method: 'POST',
  pathParams: undefined,
  queryParams: undefined,
  body: ZodRecurringInvestmentBody,
  responseBody: z.object({
    b2b_recurring_investment_id: ZodRecurringInvestmentId,
  }),
  headers: undefined,
});

export type PostExploreRecurringInvestmentsEndpoint = GetEndpointType<
  typeof postExploreRecurringInvestmentsEndpoint
>;

export const ZodRecurringInvestmentItem = z.object({
  id: ZodRecurringInvestmentId,
  instrument_logos: ZodTrimmedString.array(),
  next_execution_at: ZodDateTimeString,
  next_billing_at: ZodDateTimeString,
  frequency: ZodB2bRecurringInvestmentFrequency,
  total_amount_gross: ZodAmount,
});

export type RecurringInvestmentItem = z.infer<typeof ZodRecurringInvestmentItem>;

export const getExploreRecurringInvestmentsEndpoint = endpoint({
  method: 'GET',
  pathParams: undefined,
  queryParams: undefined,
  body: undefined,
  responseBody: z.object({
    recurring_investments: ZodRecurringInvestmentItem.array(),
  }),
  headers: undefined,
});

export type GetExploreRecurringInvestmentsEndpoint = GetEndpointType<
  typeof getExploreRecurringInvestmentsEndpoint
>;

const ZodRecurringInvestmentAllocation = z.object({
  instrument_id: ZodId,
  allocation_percent_rate: z.number(),
  amount_net: ZodAmount,
});

const ZodRecurringInvestmentPayer = z.enum(['employee', 'company']);

export const ZodRecurringInvestmentByIdResponse = z.object({
  ...ZodRecurringInvestmentItem.shape,
  created_at: ZodDateTimeString,
  plan_category: ZodB2bSavingPlanCategory,
  number_of_executions: z.number(),
  company_id: ZodId,
  company_name: ZodTrimmedString,
  payment_method: ZodB2BPaymentMethod,
  allocations: ZodRecurringInvestmentAllocation.array(),
  total_invested_amount_net: ZodAmount,
  payer: ZodRecurringInvestmentPayer,
});

export type RecurringInvestmentOrder = z.infer<typeof ZodRecurringInvestmentByIdResponse>;

export const getRecurringInvestmentsByIdEndpoint = endpoint({
  method: 'GET',
  pathParams: ZodRecurringInvestmentIdPathParam,
  responseBody: z.object({
    recurring_investment: ZodRecurringInvestmentByIdResponse,
  }),
});

export type GetRecurringInvestmentsByIdEndpoint = GetEndpointType<
  typeof getRecurringInvestmentsByIdEndpoint
>;

const ZodGetRecurringInvestmentTransactionsResponse = z.object({
  transactions: ZodProfileActivityTransaction.array(),
  cursor: ZodCursorData,
});

export const getRecurringInvestmentTransactionsEndpoint = endpoint({
  method: 'GET',
  pathParams: ZodRecurringInvestmentIdPathParam,
  responseBody: ZodGetRecurringInvestmentTransactionsResponse,
  queryParams: createZodParamsValidator(
    z.object({
      ...ZodCursorQuery.shape,
      // min(1).max(100) introduces breaking changes
      limit: z.number().int().optional(),
    }),
  ),
});

export type GetRecurringInvestmentTransactionsEndpoint = GetEndpointType<
  typeof getRecurringInvestmentTransactionsEndpoint
>;

export const deleteExploreRecurringInvestmentsIdEndpoint = endpoint({
  method: 'DELETE',
  pathParams: ZodRecurringInvestmentIdPathParam,
  responseBody: ZodSuccessResponse,
});

export type DeleteExploreRecurringInvestmentsIdEndpoint = GetEndpointType<
  typeof deleteExploreRecurringInvestmentsIdEndpoint
>;
